import React, { useEffect } from 'react';

import MButton from 'components/Mbutton';
import { BsSearch } from 'react-icons/bs';
import {
  AppBar,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Slide,
  TextField,
  Toolbar,
  Typography,
  capitalize,
} from '@mui/material';
import { CloseModalButton, NotFoundResults } from 'components/Utils';
import { REQUEST_TYPE } from 'Constaints';
import { Text } from '@chakra-ui/react';
import { formatDate } from 'utils';
import { BsBank2, BsCardHeading, BsPeople } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const SearchRequestModal = ({
  searchResults,
  searchTerm,
  setSearchTerm,
  placeholder = 'Search...',
  iconLabel,
  title = 'Rechercher',
  searchFunction,
  handleClickResult,
  loading,
}) => {
  const { t } = useTranslation('requests');
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  useEffect(() => {
    if (open) {
      const searchInput = document.getElementById('search-input');
      if (searchInput) {
        searchInput?.focus();
      }
    }
  }, [open]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm(''); // Clear search term on close
    setInputValue(''); // Clear input value
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    // Directly update the search term — debounce is handled in the parent
    setSearchTerm(value);
    searchFunction(value);
  };

  // Manual search (button click or Enter) — for explicit user intent
  const handleSearch = () => {
    if (inputValue && inputValue.trim().length > 0) {
      setSearchTerm(inputValue.trim());
      searchFunction(inputValue.trim());
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleResultClick = (item) => {
    handleClickResult(item);
    setOpen(false);
    setInputValue(''); // Clear input after selection
  };

  // Helper function to get entity icon based on request type
  const getRequestIcon = (requestEntityType) => {
    switch (requestEntityType) {
      case 'ENTERPRISE':
        return <BsBank2 size={30} className="text-blue-600" />;
      case 'EMPLOYEE':
        return <BsPeople size={30} className="text-green-600" />;
      case 'CONTRACT':
        return <BsCardHeading size={30} className="text-purple-600" />;
      default:
        return <BsSearch size={30} className="text-gray-600" />;
    }
  };

  // Helper function to get status chip
  const getStatusChip = (status) => {
    if (status === 'REJECTED') {
      return (
        <Chip
          size="small"
          color="error"
          variant="outlined"
          className="capitalize-none"
          label={capitalize(t('status_rejected'))}
        />
      );
    } else if (status === 'COMPLETED') {
      return (
        <Chip
          size="small"
          className="capitalize-none"
          variant="outlined"
          color="success"
          label={t('status_validated')}
        />
      );
    } else {
      return (
        <Chip
          size="small"
          className="capitalize-none"
          variant="outlined"
          color="warning"
          label={t('status_pending')}
        />
      );
    }
  };

  // Helper function to get primary text based on entity type
  const getPrimaryText = (item) => {
    if (item?.entityType === 'ENTERPRISE') {
      return item?.request?.socialRaison || item?.request?.enterprise?.socialRaison || 'N/A';
    } else if (item?.entityType === 'EMPLOYEE') {
      return (
        item?.request?.employer?.fullName ||
        `${item?.request?.employer?.firstName || ''} ${item?.request?.employer?.lastName || ''}`.trim() ||
        'N/A'
      );
    } else if (item?.entityType === 'CONTRACT') {
      return (
        item?.request?.contract?.employer?.fullName ||
        `${item?.request?.contract?.employer?.firstName || ''} ${item?.request?.contract?.employer?.lastName || ''}`.trim() ||
        'N/A'
      );
    }
    return 'N/A';
  };

  // Helper function to get secondary text based on entity type
  const getSecondaryText = (item) => {
    const parts = [];

    if (item?.entityType === 'ENTERPRISE') {
      if (item?.request?.nui || item?.request?.enterprise?.nui) {
        parts.push(`NIU: ${(item?.request?.nui || item?.request?.enterprise?.nui)?.toUpperCase()}`);
      }
    } else if (item?.entityType === 'EMPLOYEE') {
      if (item?.request?.employer?.enterprise?.socialRaison) {
        parts.push(item?.request?.employer?.enterprise?.socialRaison);
      }
      if (item?.request?.employer?.nationality) {
        parts.push(item?.request?.employer?.nationality);
      }
    } else if (item?.entityType === 'CONTRACT') {
      if (item?.request?.contract?.employer?.enterprise?.socialRaison) {
        parts.push(item?.request?.contract?.employer?.enterprise?.socialRaison);
      }
      if (item?.request?.contract?.employer?.nationality) {
        parts.push(item?.request?.contract?.employer?.nationality);
      }
    }

    if (item?.request?.motif) {
      parts.push(`Motif: ${item.request?.motif}`);
    }

    return parts.join(' • ');
  };

  return (
    <>
      <MButton onClick={handleClickOpen}>
        {' '}
        <BsSearch /> {iconLabel}{' '}
      </MButton>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        // sx={{ marginTop: -40 }}
        fullWidth
        maxWidth={'md'}
      >
        <AppBar sx={{ backgroundColor: 'white', position: 'relative' }}>
          <Toolbar className="bg-gray-50">
            <Typography sx={{ ml: 2, flex: 1 }} color={'green'} variant="h6" component="div">
              {title}
            </Typography>
            {/* <CloseModalButton
                            handleClose={handleClose}
                        /> */}
            <CloseModalButton iconColor={'red'} handleClose={handleClose} />
          </Toolbar>
          <Divider />
          <div className="flex justify-center mt-3 mb-3 gap-2 px-4">
            <TextField
              style={{ width: '70%' }}
              className="shadow"
              value={inputValue}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              id="search-input"
              label={placeholder}
              type="search"
              focused
              autoFocus
            />
            <MButton
              onClick={handleSearch}
              disabled={!inputValue || inputValue.trim().length === 0}
            >
              <BsSearch /> {t('search_button') || 'Rechercher'}
            </MButton>
          </div>
        </AppBar>

        <DialogContent>
          {searchResults?.length > 0 ? (
            <List className="mt-5">
              {searchResults?.map((item) => (
                <ListItem
                  className="shadow cursor-pointer hover:scale-105 hover:font-bold hover:bg-gray-200 transition duration-300 mb-2"
                  onClick={() => handleResultClick(item)}
                  key={item?.request?.id}
                >
                  <ListItemAvatar>{getRequestIcon('EMPLOYEE')}</ListItemAvatar>

                  <ListItemText
                    primary={
                      <div className="flex items-center gap-2">
                        <span className="font-semibold capitalize">{getPrimaryText(item)}</span>
                        {getStatusChip(item.request?.status)}
                      </div>
                    }
                    secondary={
                      <div className="flex flex-col gap-1 mt-1">
                        <span className="text-sm">{getSecondaryText(item)}</span>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-600">
                            ID: <span className="font-mono">{item.request?.requestId}</span>
                          </span>
                          <span>•</span>
                          <span className="text-gray-600">
                            Type: {REQUEST_TYPE[item.request?.requestType]?.label || 'N/A'}
                          </span>
                          <span>•</span>
                          <span className="text-gray-600">
                            Date: {formatDate(item.request?.createdAt)}
                          </span>
                        </div>
                      </div>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : loading ? (
            <div className="flex justify-center items-center p-8">
              <Text>{t('searching') || 'Recherche en cours...'}</Text>
            </div>
          ) : searchTerm?.length === 0 ? (
            <div className="flex justify-center items-center p-8 text-gray-500">
              <Text>{t('enter_search_term') || 'Entrez un terme de recherche'}</Text>
            </div>
          ) : (
            <NotFoundResults />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SearchRequestModal;
